import { useTranslation } from 'react-i18next';
import notification from 'ui-library/components/Notification';
import SM from 'services/ServiceManager';
import { DefaultError, useMutation, useQueryClient } from '@tanstack/react-query';

export interface Payload<T> {
    service: string;
    API: string;
    payload?: T;
    invalidateQueryKeys?: string[] | null;
    setQueryData?: {
        queryKey: (string | number)[];
        queryData: (old: any) => any;
    } | null;
}

export type requestType = 'post' | 'put' | 'delete' | 'patch';

export interface Response<T> {
    data: T;
}

export interface DefaultPayload<V> {
    [key: string]: V;
}

export const useGenericAPIMutation = <T, U, V>(
    defaultPayload: DefaultPayload<V> = {}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useMutation<Response<U>, DefaultError, Payload<T>>({
        mutationFn: async ({ service, API, payload }: Payload<T>) => {
            const argsArray = Array.isArray(payload)
                ? payload
                : [{ ...defaultPayload, ...payload }];
            const response: any = await SM?.[service](API, argsArray);

            return response;
        },
        onSuccess: (_data, variables) => {
            // Invalidate and refetch using keys provided in the mutation variables
            const keys = variables?.invalidateQueryKeys ?? [];
            const updateQueryParams = variables?.setQueryData ?? null;

            if (keys.length) {
                keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
            }

            if (
                updateQueryParams?.queryKey?.length
                && updateQueryParams?.queryData
            ) {
                queryClient.setQueryData(
                    updateQueryParams.queryKey,
                    (old: any) => updateQueryParams.queryData(old),
                );
            }
        },
        onError: (error: any) => {
            notification.open({
                content:
                    error?.response?.data?.Properties?.ValidationErrors?.[0]
                        ?.Message
                    || error?.response?.data?.message
                    || (typeof error?.response?.data === 'string' ? error?.response?.data : null)
                    || error?.message
                    || `${t('contactGroups.somethingWentWrong')}`,
                type: 'error',
            });

            return error;
        },
    });
};
